import { useState, useCallback, useEffect, useRef } from "react";
import { useAuth } from "./useAuth";

/**
 * Fetch and manage actions (AccountAlerts) for an account.
 *
 * @param {string} accountId - Account UUID
 * @param {Object} [options]
 * @param {string[]} [options.statusFilter] - Filter by status values.
 *   Must be a stable reference (module-level constant or useMemo).
 *   Omit to get default.
 * @param {boolean} [options.includeRejected=false] - Also return rejected
 *   (feedback_state="dismissed") rows: sends include_rejected=1 on every fetch.
 */
export const useActions = (
  accountId,
  { statusFilter, includeRejected = false } = {},
) => {
  const { apiClient } = useAuth();
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Stabilize statusFilter by serializing to string for deps comparison
  const filterKey = statusFilter?.slice().sort().join(",") ?? "";
  const statusFilterRef = useRef(statusFilter);
  statusFilterRef.current = statusFilter;
  const includeRejectedRef = useRef(includeRejected);
  includeRejectedRef.current = includeRejected;

  const fetchActions = useCallback(async () => {
    if (!accountId) return;

    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      const currentFilter = statusFilterRef.current;
      if (currentFilter?.length) {
        currentFilter.forEach((s) => params.append("status", s));
      }
      if (includeRejectedRef.current) params.append("include_rejected", "1");
      const qs = params.toString();
      const url = `accounts/${accountId}/actions/${qs ? `?${qs}` : ""}`;

      const response = await apiClient.get(url);
      setActions(response.data?.results || response.data || []);
    } catch (err) {
      console.error("Error fetching actions:", err);
      if (err.response?.status === 404) {
        setActions([]);
      } else if (err.response?.status !== 401) {
        setError("Failed to fetch actions");
      }
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiClient, accountId, filterKey, includeRejected]);

  const submitFeedback = useCallback(
    async (actionId, isIssue, feedbackText = null) => {
      try {
        const payload = { is_issue: isIssue };
        if (feedbackText) {
          payload.feedback_text = feedbackText;
        }
        const response = await apiClient.post(
          `accounts/${accountId}/actions/${actionId}/feedback/`,
          payload,
        );
        await fetchActions();
        return response.data;
      } catch (err) {
        console.error("Error submitting feedback:", err);
        throw err;
      }
    },
    [apiClient, accountId, fetchActions],
  );

  const updateActionItem = useCallback(
    async (actionId, index, { status, note } = {}) => {
      try {
        const payload = { index };
        if (status !== undefined) payload.status = status;
        if (note !== undefined) payload.note = note;
        const response = await apiClient.post(
          `actions/${actionId}/update-action-item/`,
          payload,
        );
        return response.data;
      } catch (err) {
        console.error("Error updating action item:", err);
        throw err;
      }
    },
    [apiClient],
  );

  useEffect(() => {
    fetchActions();
  }, [fetchActions]);

  return {
    actions,
    loading,
    error,
    refetch: fetchActions,
    submitFeedback,
    updateActionItem,
  };
};
